import { createSlice, isRejected } from "@reduxjs/toolkit";
import type { UnknownAction } from "@reduxjs/toolkit";

import type { RootState } from "store/store";

// AIDEV-NOTE: `api/generated` musí být před `api/api` — oba se importují navzájem a generated
// z `api` hned při načtení volá `injectEndpoints`. Obráceně by dostal ještě nevytvořené `api`.
import { api } from "api/generated";
import { isStudentEndpoint } from "api/api";

import { isInvalidTokenMessage } from "utils/invalidTokenMessage";

/**
 * Backend studentův token zneplatnil, protože se přihlásil jinde (každé `userLogin` vydá nový).
 * „shown“ otevře modal „Jste přihlášeni jinde“, „dismissed“ ho po zavření drží zavřený —
 * dotazů selže při načtení stránky několik naráz a každý další by ho jinak otevřel znovu.
 * Stav se nuluje jen plným načtením stránky (nový přihlášený student = nové načtení). [TD-460]
 */
export type InvalidTokenState = "idle" | "shown" | "dismissed";

// AIDEV-NOTE: Porovnává se `errors[].message` z odpovědi, ne `message` chyby — ten
// graphql-request skládá i z celého requestu, tedy z proměnných s textem odpovědi studenta
// (stejný princip jako `getConflict` v sessionConflict.ts). Selhaný přenos (Failed to fetch)
// `errors` nemá, takže neplatný token není. [TD-460]
const hasInvalidTokenError = (meta: unknown): boolean => {
  const errors = (meta as { baseQueryMeta?: { response?: { errors?: Array<{ message?: unknown }> } } } | undefined)
    ?.baseQueryMeta?.response?.errors;

  return (errors ?? []).some(error => typeof error.message === "string" && isInvalidTokenMessage(error.message));
};

// AIDEV-NOTE: Jakýkoli zamítnutý endpoint RTK Query, ne výčet jako v sessionConflict — token
// zneplatní kdykoli, u libovolného dotazu. Rodičovské endpointy, endpointy učitele a `login`
// (handshake ze Zóny v Authenticatoru) studentský token neposílají, o přihlášení jinde u nich
// nejde — to dál řeší `processApiError` přesměrováním. [TD-460]
const isRejectedStudentRequest = (action: UnknownAction): boolean => {
  if (!isRejected(action) || !action.type.startsWith(`${api.reducerPath}/`)) return false;

  const endpointName = (action.meta.arg as { endpointName?: unknown } | undefined)?.endpointName;

  return typeof endpointName === "string" && isStudentEndpoint(endpointName);
};

const initialState = "idle" as InvalidTokenState;

const invalidTokenSlice = createSlice({
  name: "invalidToken",
  initialState,
  reducers: {
    dismissInvalidToken: () => "dismissed" as InvalidTokenState,
  },
  extraReducers: builder => {
    builder.addMatcher(isRejectedStudentRequest, (state, action) =>
      state === "idle" && hasInvalidTokenError((action as { meta?: unknown }).meta) ? "shown" : state,
    );
  },
});

export const { dismissInvalidToken } = invalidTokenSlice.actions;

export const selectInvalidToken = (state: RootState) => state.invalidToken;

export default invalidTokenSlice.reducer;
